import { ApiService } from './api';

export type SenderHealthLevel = 'quiet' | 'early' | 'healthy' | 'watch' | 'stop';

/** Seven-day sending health per address. Mirrors server/src/lib/sender-health.ts. */
export type SenderHealth = {
  level: SenderHealthLevel;
  sent7d: number;
  bounced7d: number;
  replied7d: number;
  blocked7d: number;
  lastBlockReason: string | null;
  bounceRate: number | null;
  replyRate: number | null;
  reason: string;
};

export type EmailAccount = {
  account_id: string;
  user_id: string;
  email: string;
  display_name: string;
  smtp_host: string;
  smtp_port: number;
  username: string;
  password?: string;
  use_ssl: boolean;
  status: string;
  created_at: string;
  updated_at: string;
  is_warming: boolean;
  emails_sent?: number;
  warmup_emails?: number;
  /** Whether the warmup ramp applies to this account. Owner-controlled. */
  warmup_throttle?: boolean;
  /** Ramp state, computed server-side. Null when throttling is switched off. */
  warmup?: {
    sendingDay: number;
    ramping: boolean;
    dailyCap: number | null;
    fullSpeedOn: string | null;
  } | null;
  health_score?: number;
  /** Seven-day sending health, computed server-side. */
  health?: SenderHealth;
};

export type WarmupStatus = {
  account_id: string;
  status: string;
  is_warming: boolean;
  emails_sent: number;
  health_score: number;
  landed_inbox: number;
  landed_spam: number;
};

export type VitalsResult = {
  mx: boolean;
  spf: boolean;
  dkim: boolean;
  dmarc: boolean;
};

export type CreateEmailAccountData = {
  email: string;
  displayName?: string;
  smtpHost: string;
  smtpPort: number;
  username: string;
  password: string;
  useSSL?: boolean;
};

class EmailAccountService extends ApiService {
  private emailAccountsUrl = '/api/email-accounts';

  constructor() {
    super();
  }

  async getEmailAccounts(): Promise<EmailAccount[]> {
    return this.get<EmailAccount[]>(this.emailAccountsUrl);
  }

  async createEmailAccount(data: CreateEmailAccountData): Promise<EmailAccount> {
    const response = await this.post<{ account_id: string; status: string; message: string }>(this.emailAccountsUrl, data);
    // After creation, fetch the account to get full details
    const accounts = await this.getEmailAccounts();
    const account = accounts.find(acc => acc.account_id === response.account_id);
    if (!account) {
      throw new Error('Account created but could not be retrieved');
    }
    return account;
  }

  async deleteEmailAccount(id: string): Promise<void> {
    return this.delete<void>(`${this.emailAccountsUrl}/${id}`);
  }

  async enableWarmup(accountId: string): Promise<void> {
    await this.post<void>(`${this.emailAccountsUrl}/${accountId}/warmup/enable`);
  }

  async disableWarmup(accountId: string): Promise<void> {
    await this.post<void>(`${this.emailAccountsUrl}/${accountId}/warmup/disable`);
  }

  async getWarmupStatus(accountId: string): Promise<WarmupStatus> {
    return this.get<WarmupStatus>(`${this.emailAccountsUrl}/${accountId}/warmup/status`);
  }

  async getWarmupVitals(accountId: string): Promise<VitalsResult> {
    return this.get<VitalsResult>(`${this.emailAccountsUrl}/${accountId}/warmup/vitals`);
  }
}

export const emailAccountService = new EmailAccountService();

